import { useState } from "react";
import {
  SqDropdownMenu,
  SqDropdownMenuContent,
  SqDropdownMenuGroup,
  SqDropdownMenuItem,
  SqDropdownMenuPortal,
  SqDropdownMenuTrigger,
} from "@/components/squircle-ui/dropdown";
import {
  RiArrowDownSLine,
  RiTableFill,
  RiTableLine,
  RiLandscapeAiFill,
  RiBarChart2Fill,
  RiLineChartFill,
  RiPercentFill,
} from "@remixicon/react";
import { ViewMode } from "../../types";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

interface ViewModeOption {
  key: ViewMode;
  label: string;
  description: string;
  icon: typeof RiTableLine;
  section: "DATA ONLY" | "CREATIVE VISUALIZATION" | "TIME SERIES";
}

interface Props {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  metadata?: Partial<
    Record<
      ViewMode,
      {
        hide?: boolean;
        disabled?: boolean;
        disablingReason?: string;
      }
    >
  >;
}

const viewModeOptions: ViewModeOption[] = [
  {
    key: "table_view",
    label: "Table View",
    description: "View all metrics in a sortable table.",
    icon: RiTableLine,
    section: "DATA ONLY",
  },
  {
    key: "creative_view",
    label: "Creative View",
    description: "See each creative with its key performance metrics.",
    icon: RiLandscapeAiFill,
    section: "CREATIVE VISUALIZATION",
  },
  {
    key: "bar_chart_view",
    label: "Bar Chart",
    description: "Compare creatives side-by-side using bar chart.",
    icon: RiBarChart2Fill,
    section: "CREATIVE VISUALIZATION",
  },
  {
    key: "line_chart_view",
    label: "Line Chart",
    description: "Track how your metrics change over time.",
    icon: RiLineChartFill,
    section: "TIME SERIES",
  },
  {
    key: "creative_share_view",
    label: "Creative Share",
    description: "See each creative/group share of total volume.",
    icon: RiPercentFill,
    section: "TIME SERIES",
  },
];

const ViewModeDropdown = ({ view, onViewChange, metadata }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = viewModeOptions.find((o) => o.key === view);
  const SelectedIcon = selectedOption?.icon || RiTableLine;

  // Filter options based on metadata
  const visibleOptions = viewModeOptions.filter(
    (option) => !metadata?.[option.key]?.hide,
  );

  // Group options by section
  const dataOnlyOptions = visibleOptions.filter((o) => o.section === "DATA ONLY");
  const creativeVisualizationOptions = visibleOptions.filter(
    (o) => o.section === "CREATIVE VISUALIZATION",
  );
  const timeSeriesOptions = visibleOptions.filter((o) => o.section === "TIME SERIES");

  // Helper function to render a section
  const renderSection = (
    sectionTitle: string,
    options: ViewModeOption[],
    iconOverride?: typeof RiTableLine,
  ) => {
    if (options.length === 0) return null;

    return (
      <div className="">
        <div className="flex justify-start items-center gap-[10px] px-2 pt-[6px] pb-3 text-brand-primary-400 text-[11px] font-medium uppercase tracking-[0.2px]">
          {sectionTitle}
        </div>
        <SqDropdownMenuGroup>
          {options.map((option) => {
            const OptionIcon = iconOverride || option.icon;
            const isSelected = view === option.key;
            const isDisabled = !!metadata?.[option.key]?.disabled;

            return (
              <SqDropdownMenuItem
                key={option.key}
                className={cn(
                  "h-[42px] px-2 py-[6px] flex items-center gap-2 cursor-pointer",
                  isSelected &&
                    "squircle-fill-selected-gray hover:squircle-fill-selected-gray",
                  isDisabled && "opacity-50 cursor-not-allowed",
                )}
                onClick={() => {
                  if (!isDisabled) {
                    onViewChange(option.key);
                    setIsOpen(false);
                  }
                }}
                disabled={isDisabled}
              >
                <div className="flex items-center gap-2 w-full">
                  <OptionIcon
                    size={16}
                    className={cn(
                      "shrink-0",
                      isSelected ? "text-brand-orange" : "text-brand-primary-600",
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium leading-normal text-brand-primary-800">
                      {option.label}
                    </div>
                    <div className="mt-0.5 text-[11px] line-clamp-1 text-brand-primary-600 font-normal not-italic leading-normal">
                      {option.description}
                    </div>
                  </div>
                </div>
              </SqDropdownMenuItem>
            );
          })}
        </SqDropdownMenuGroup>
      </div>
    );
  };

  return (
    <SqDropdownMenu modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <SqDropdownMenuTrigger className="pl-1">
        <div className="mr-2 w-full flex items-center gap-2 overflow-hidden">
          <div className="flex items-center justify-center w-6 h-6 rounded-[6px] border border-[#FFDACC] bg-[#FFF0EB]">
            <SelectedIcon size={14} className="shrink-0 text-brand-orange" />
          </div>
          <span className="flex-1 truncate text-brand-primary-new text-[12px] font-medium leading-none tracking-[0.24px]">
            {selectedOption?.label || "Table View"}
          </span>
        </div>
        <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
      </SqDropdownMenuTrigger>
      <SqDropdownMenuPortal>
        <SqDropdownMenuContent
          side="bottom"
          align="start"
          className="w-auto p-1"
          radius={12}
          collisionPadding={10}
        >
          {/* DATA ONLY Section */}
          {renderSection("DATA ONLY", dataOnlyOptions, RiTableFill)}
          <Separator className="my-[2px]" />

          {/* CREATIVE VISUALIZATION Section */}
          {renderSection("CREATIVE VISUALIZATION", creativeVisualizationOptions)}
          <Separator className="my-[2px]" />
          {/* TIME SERIES Section */}
          {renderSection("TIME SERIES", timeSeriesOptions)}
        </SqDropdownMenuContent>
      </SqDropdownMenuPortal>
    </SqDropdownMenu>
  );
};

export default ViewModeDropdown;
